import { ReactNode } from 'react';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import moment from 'moment-timezone';
import { pxToRem } from 'utils/convert';
import { DATE_FORMAT_FULL_WITH_TIME } from 'utils/date';
import { fieldMap, USAGE_TABLE_COLUMNS } from './constants';

import {
  FieldInvalidFailure,
  FieldInvalidType,
  HasCycleKeys,
  SnippetFailures,
  SnippetFields,
  SnippetUsage,
  SnippetValidationFailure,
  SnippetValidation,
  ValidationData,
  ValidationFailure,
  ResultType,
  CsvRow,
} from './types';

/**
 * Returns a text slug generated from Snippet name and dynamic fields.
 *
 * @param {string} snippetName The name of the snippet.
 * @param {array} dynamicFields Positional parameters/variables added to a created snippet.
 */

export const generateSlug = (snippetName: string, dynamicFields: string[] | null): string => {
  const content = dynamicFields?.length
    ? `snippet "${snippetName}" ${dynamicFields.join(' ')}`
    : `snippet "${snippetName}"`;
  return `{{{ ${content} }}}`;
};

/** Generates remaining character count to display for name and description fields */
export const getRemainingCharactersText = (
  maxCharacters: number,
  usedCharacters: number,
): string => {
  const remainingCharacters = maxCharacters - usedCharacters;
  return `${remainingCharacters >= 0 ? remainingCharacters : 0} characters`;
};

export const getSnippetHeaderGrid = (showOverflow: boolean) => `
  minmax(${pxToRem(200)}, 1fr)
  minmax(${pxToRem(200)}, 1.5fr)
  ${pxToRem(200)}
  ${showOverflow ? pxToRem(36) : ''}
`;

export const getSnippetRowGrid = (showOverflow: boolean) => `
  minmax(${pxToRem(200)}, 1fr)
  minmax(${pxToRem(200)}, 1.5fr)
  ${pxToRem(200)}
  ${showOverflow ? pxToRem(36) : ''}
`;

export const getUsageGrid = () => `
  minmax(${pxToRem(200)}, 3.5fr)
  minmax(${pxToRem(100)}, 1.5fr)
  minmax(${pxToRem(100)}, 1.5fr)
  minmax(${pxToRem(100)}, 1.5fr)
  minmax(${pxToRem(200)}, 1.5fr)
`;

/** Returns true if snippet name is valid (contains only alphanumeric values, spaces, underscroes, and dashes) */
export const checkValidSnippetName = (snippetName: string) =>
  snippetName.length <= 100 && !!snippetName.match(/^[\w\-\s]+$/);

const getFieldInvalidMsg = (err: FieldInvalidFailure): string | ReactNode => {
  switch (err.errorType?.__typename) {
    case FieldInvalidType.AnchorTagsWithJS:
      return `${
        fieldMap[err.field || 'default']?.name
      } cannot contain links with JavaScript tags.   [<a href="javascript:...">]`;

    case FieldInvalidType.HasCycle:
      return `Snippets cannot reference themselves.`;

    case FieldInvalidType.HasScriptTag:
      return `${fieldMap[err.field || 'default']?.name} cannot contain <script> tags.`;

    case FieldInvalidType.IframeTagWithJS: {
      return `${
        fieldMap[err.field || 'default']?.name
      } cannot contain JavaScript within an <iframe> tag.`;
    }

    case FieldInvalidType.IllegalCharacters: {
      switch (err.field) {
        case SnippetFields.name:
          return `${
            fieldMap[SnippetFields.name].name
          } can only contain letters, numbers, spaces, dashes, and underscores.`;
        case SnippetFields.positionalParameters:
          return `${
            fieldMap[SnippetFields.positionalParameters].name
          } can only contain letters, numbers, dashes, and underscores.`;
        default:
          return `${fieldMap[err.field || 'default']?.name} contains illegal characters`;
      }
    }

    case FieldInvalidType.NotRenderable: {
      return `${fieldMap[SnippetFields.content].name} contains invalid HTML.`;
    }

    default:
      return `${fieldMap[err.field || 'default']?.name} is invalid`;
  }
};

export const getValidationData = (err: SnippetValidationFailure): ValidationData => {
  switch (err.__typename) {
    case SnippetFailures.FieldInvalidFailure:
      return {
        errorMsg: getFieldInvalidMsg(err),
        fieldId: fieldMap[err.field || 'default']?.fieldId,
        typeName: err.__typename,
      };

    case SnippetFailures.FieldNotProvidedFailure:
      return {
        errorMsg: `${fieldMap[err.field || 'default']?.name} is required.`,
        fieldId: fieldMap[err.field || 'default']?.fieldId,
        typeName: err.__typename,
      };

    case SnippetFailures.FieldTooLongFailure:
      return {
        errorMsg: `${fieldMap[err.field || 'default']?.name} can't be longer than ${
          fieldMap[err.field || 'default']?.maxLength
        } characters.`,
        fieldId: fieldMap[err.field || 'default']?.fieldId,
        typeName: err.__typename,
      };

    case SnippetFailures.SnippetIdDoesNotExistFailure: {
      return {
        errorMsg: `The snippet with id ${err.Id} does not exist.`,
        fieldId: '',
        typeName: err.__typename,
      };
    }

    case SnippetFailures.SnippetNameAlreadyExistsFailure: {
      return {
        errorMsg: `${fieldMap[SnippetFields.name]?.name} must be unique.`,
        fieldId: fieldMap[SnippetFields.name]?.fieldId,
        typeName: err.__typename,
      };
    }

    case SnippetFailures.SnippetParameterDuplicateFailure: {
      return {
        errorMsg: `${fieldMap[SnippetFields.positionalParameters]?.name} must be unique.`,
        fieldId: fieldMap[SnippetFields.positionalParameters]?.fieldId,
        typeName: err.__typename,
      };
    }

    default: {
      return {
        errorMsg: `An error occurred.`,
        fieldId: '',
        typeName: err.__typename,
      };
    }
  }
};

export const getFailedValidationMessage = (resp: ValidationFailure) => {
  switch (resp.type) {
    case SnippetValidation.NotRenderable:
      return 'This snippet contains invalid Handlebars.';
    case SnippetValidation.HasCycle:
      if (resp.errorMsg.includes(HasCycleKeys.ContainsLoop)) {
        return "Snippets can't reference themselves.";
      } else if (resp.errorMsg.includes(HasCycleKeys.FailedToProcess)) {
        return 'This snippet contains an invalid snippet.';
      } else if (resp.errorMsg.includes(HasCycleKeys.InvalidSnippet)) {
        return 'This snippet contains missing or deleted content.';
      }

      return 'An error occurred';

    default:
      return 'An error occurred.';
  }
};

const getHasCycleMessage = (errorMsg: string) => {
  let snippetName;

  if (errorMsg.includes(HasCycleKeys.ContainsLoop)) {
    snippetName = errorMsg.replace(/(.*)→/, '').trim();
    return {
      msg: `Cannot include the name of the same snippet:`,
      code: `{{{ snippet "${snippetName}" }}}`,
    };
  } else if (errorMsg.includes(HasCycleKeys.FailedToProcess)) {
    snippetName = errorMsg.replace(/(.*)→/, '').trim();
    return {
      msg: `We were unable to process this snippet:`,
      code: `{{{ snippet "${snippetName}" }}}`,
    };
  } else if (errorMsg.includes(HasCycleKeys.InvalidSnippet)) {
    // eslint-disable-next-line prefer-destructuring
    snippetName = errorMsg.split("'")[1];

    return {
      msg: `We couldn't find this snippet in your project:`,
      code: `{{{ snippet "${snippetName}" }}}`,
    };
  }

  return { msg: 'An error occurred.' };
};

export const getFailedValidationBannerMessage = (resp: ValidationFailure) => {
  switch (resp.type) {
    case SnippetValidation.NotRenderable:
      // eslint-disable-next-line no-case-declarations
      const modifiedMsg = resp.errorMsg.replace(
        'Snippet content contains invalid merge parameter(s):',
        '',
      );
      return { msg: 'Some Handlebars code is formatted incorrectly:', code: modifiedMsg };
    case SnippetValidation.HasCycle:
      return getHasCycleMessage(resp.errorMsg);
    default:
      return { msg: 'An error occurred.' };
  }
};

export const getReferenceUrl = ({ resultType, id, campaignID }: SnippetUsage) => {
  switch (resultType) {
    case 'Campaign':
      return `/templates/edit?templateId=${id}&campaignId=${campaignID}`;

    case 'Template':
      return `/templates/edit?templateId=${id}`;

    default:
      return `/templates/snippet/${id}/edit`;
  }
};

export const splitArrayIntoSubArrays = (arr: any, len: number) => {
  const chunks = [];
  const n = arr.length;
  let i = 0;

  while (i < n) {
    chunks.push(arr.slice(i, (i += len)));
  }

  return chunks;
};

export const handleReferenceSort = (
  rows: SnippetUsage[],
  type: string,
  direction: SORT_DIRECTIONS,
) => {
  const order = direction === SORT_DIRECTIONS.ASCENDING ? 'asc' : 'desc';

  // Add function as iteratee to make name sort case insensitive
  if (type === 'name') {
    return orderBy(rows, [(row) => row.name.toLowerCase()], [order]);
  }

  return orderBy(rows, [type], [order]);
};

/**
 * @param campaignId - Used to determine whether the Snippet is used in a campaign.
 * @param messageMedium - Along with the standard MESSAGE_MEDIUMS, findSnippetUsage query returns 'Snippet' to identify if the snippet is nested.
 */

const findResultType = (campaignId: number | null, messageMedium: string) => {
  if (campaignId) {
    return ResultType.Campaign;
  } else if (messageMedium === ResultType.Snippet) {
    return ResultType.Snippet;
  } else {
    return ResultType.Template;
  }
};

export const addResultTypeToRow = (rows: SnippetUsage[]) => {
  return rows.map((data: SnippetUsage) => {
    const resultType = findResultType(data.campaignID, data.messageMedium);
    return {
      ...data,
      resultType,
    };
  });
};

export const updateRowNames = (rows: SnippetUsage[]) => {
  return rows.map((data: SnippetUsage) => {
    return {
      ...data,
      name: data.campaignName || data.name,
    };
  });
};

export const getRowsToDisplay = (pages: SnippetUsage[][], activePage: number) => {
  if (!isEmpty(pages)) {
    const page = pages[activePage - 1];
    /** Add "Type" to each row: Snippet, Campaign, or Template */
    return addResultTypeToRow(page);
  } else {
    return [];
  }
};

export const getCSVdata = (rows: SnippetUsage[]): CsvRow[] => {
  const { origin } = window.location;
  return rows.map((row: SnippetUsage) => {
    const path = getReferenceUrl(row);

    const metricData: CsvRow = {
      id: row.id,
      name: row.name,
      messageMedium: row.messageMedium,
      resultType: row.resultType,
      campaignState: row.campaignState ? row.campaignState : 'Inactive',
      updatedAt: moment(row.updatedAt).format(DATE_FORMAT_FULL_WITH_TIME),
      url: `${origin}${path}`,
    };

    return metricData;
  });
};

export const parseColsToCSVHeaders = () => {
  const headerArr: any = [{ label: 'ID', key: 'id' }];
  USAGE_TABLE_COLUMNS.forEach((col) => {
    headerArr.push({
      label: col.label,
      key: col.id,
    });
  });

  headerArr.push({
    label: 'URL',
    key: 'url',
  });

  return headerArr;
};
